import React, { useState } from "react";
import { Dialog, LoadingSkeleton, Tabs, Toggle } from "@narmi/design_system";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { useSearchParams } from "react-router-dom-v5-compat";
import { useNotificationContext } from "cerulean";
import HistoryTable from "./HistoryTable";
import useUserConfiguration from "./hooks/useUserConfiguration";
import useInstitutionSetting from "./hooks/useInstitutionSetting";
import styles from "./CustomSettingPage.module.css";

const SETTING_NAME = "LENDING_ASK_ADDITIONAL_FIELDS";

type FieldOption = {
  name: string;
  label: string;
  description: string;
};

type FieldValue = {
  name: string;
  value: boolean;
};

const LENDING_FIELDS: FieldOption[] = [
  {
    name: "captcha",
    label: "Captcha",
    description:
      "Show a CAPTCHA challenge on the lending personal information pages before applicants can continue.",
  },
  {
    name: "is_spouse",
    label: "Co-applicant Spouse information",
    description:
      "Show a checkbox on the co-applicant details page asking whether the co-applicant is the applicant's spouse.",
  },
  {
    name: "auto_requested_loan_amount",
    label: "Auto requested loan amount",
    description:
      "Show a requested loan amount step for still-shopping auto loan applicants in the non-refinance flow.",
  },
  {
    name: "additional_employment_details",
    label: "Additional Employment Details",
    description:
      "Collect job title, employer name, and employment start date on the employment details step, and income on its own step. When disabled, the employment details step collects only employment type and income, and additional income sources are turned off.",
  },
  {
    name: "collect_vin",
    label: "Collect VIN",
    description:
      "Show an optional VIN field on the auto loan vehicle details page. A valid VIN prefills " +
      "the year, make, model, and trim; applicants without a VIN can still enter details manually.",
  },
  {
    name: "additional_income_sources",
    label: "Additional income sources",
    description:
      'Allow applicants to add supplemental income sources (e.g. "Alimony", "Investments") beyond their primary income on the income page. Requires Additional Employment Details to be enabled. Configure the dropdown options and per-applicant cap with the "Lending Additional Income Types" and "Lending Additional Income Max Sources" settings.',
  },
  {
    name: "mla_question",
    label: "MLA military status question",
    description:
      "Ask applicants whether they or their co-applicant are an active duty servicemember or dependent (MLA covered borrower) on a screen after eligibility. Covered borrowers cannot continue a credit card application; the answer is sent to the decisioning platform for all products.",
  },
];

const previewStyles = {
  variables: {
    light: {
      addedBackground: "#fff",
      removedBackground: "#fff",
      addedGutterBackground: "#fff",
      removedGutterBackground: "#f7f7f7",
      wordAddedBackground: "#37B374",
      wordRemovedBackground: "#D93B3B",
      emptyLineBackground: "#D93B3B",
    },
  },
};

const parseSettingValue = (value: string): FieldValue[] => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? (parsed as FieldValue[]) : [];
  } catch {
    return [];
  }
};

const labelForFieldName = (fieldName: string) =>
  LENDING_FIELDS.find((f) => f.name === fieldName)?.label || fieldName;

function LendingAskAdditionalField() {
  const { setting, save } = useInstitutionSetting(SETTING_NAME);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingFieldName, setPendingFieldName] = useState<string | null>(null);
  const selectedTab = Number(searchParams.get("tab")) || 0;
  const [oldPreviewValue, setOldPreviewValue] = React.useState("");
  const [newPreviewValue, setNewPreviewValue] = React.useState("");
  const [previewIsOpen, setPreviewIsOpen] = React.useState(false);
  const [historyVersion, setHistoryVersion] = React.useState(0);
  const { sendNotification } = useNotificationContext();

  const { currentUser } = useUserConfiguration();

  const settingArray = setting ? parseSettingValue(setting.value) : [];

  const getValueForFieldName = (fieldName: string) =>
    settingArray.find((s) => s.name === fieldName)?.value ?? false;

  const updateValue = async (fieldName: string, value: boolean) => {
    const oldSettingIdx = settingArray.findIndex((s) => s.name === fieldName);

    const newSettingArray = [...settingArray];
    if (oldSettingIdx < 0) {
      newSettingArray.push({ name: fieldName, value });
    } else {
      newSettingArray[oldSettingIdx] = { ...settingArray[oldSettingIdx], value };
    }

    setPendingFieldName(fieldName);
    try {
      await save(
        JSON.stringify(newSettingArray),
        `[AUTOMATED] ${currentUser.email} updated ${fieldName} to ${value}`
      );
      setHistoryVersion((version) => version + 1);
      sendNotification({
        type: "success",
        text: `${labelForFieldName(fieldName)} ${value ? "enabled" : "disabled"}.`,
      });
    } catch (e) {
      sendNotification({
        type: "negative",
        text: e instanceof Error ? e.message : "Failed to save setting.",
      });
    } finally {
      setPendingFieldName(null);
    }
  };

  const setOldAndNewPreviewValues = (oldValue: string, newValue: string) => {
    setOldPreviewValue(oldValue);
    setNewPreviewValue(newValue);
    setPreviewIsOpen(true);
  };

  return (
    <div className={`${styles.page} nds-typography`}>
      <div className="margin--bottom--m" style={{ display: "flex", alignItems: "center" }}>
        <h2 style={{ marginTop: "0.5rem" }}>Add a field to lending applications</h2>
      </div>
      <div className="padding--all--m">
        <Tabs
          selectedIndex={selectedTab}
          onTabChange={(index) => {
            setSearchParams((params) => {
              const nextParams = new URLSearchParams(params);
              nextParams.set("tab", String(index));
              return nextParams;
            });
          }}
        >
          <Tabs.List>
            <Tabs.Tab key="fields" label="Lending" tabId="fields" />
            <Tabs.Tab key="change-log" label="Change log" tabId="change-log" />
          </Tabs.List>
          <Tabs.Panel tabId="fields">
            {!setting ? (
              <LoadingSkeleton isLoading />
            ) : (
              <div className={styles.toggleGrid}>
                {LENDING_FIELDS.sort((a, b) => a.name.localeCompare(b.name)).map((field) => (
                  <AdditionalFieldToggle
                    key={field.name}
                    field={field}
                    value={getValueForFieldName(field.name)}
                    isSaving={pendingFieldName === field.name}
                    isDisabled={pendingFieldName !== null && pendingFieldName !== field.name}
                    updateValue={updateValue}
                  />
                ))}
              </div>
            )}
          </Tabs.Panel>
          <Tabs.Panel tabId="change-log">
            <HistoryTable
              key={historyVersion}
              settingName={SETTING_NAME}
              setIsHistoryDisplayed={() => {}} // no-op since this component doesn't manage history display state
              setOldAndNewPreviewValues={setOldAndNewPreviewValues}
              sendNotification={sendNotification}
            />
            <Dialog
              isOpen={previewIsOpen}
              title="Preview"
              onUserDismiss={() => setPreviewIsOpen(false)}
              width="100%"
            >
              <div>
                <ReactDiffViewer
                  oldValue={oldPreviewValue}
                  newValue={newPreviewValue}
                  splitView
                  showDiffOnly={false}
                  styles={previewStyles}
                  compareMethod={DiffMethod.WORDS}
                  leftTitle="Current value"
                  rightTitle="New value"
                />
              </div>
            </Dialog>
          </Tabs.Panel>
        </Tabs>
      </div>
    </div>
  );
}

function AdditionalFieldToggle({
  field,
  value,
  isSaving,
  isDisabled,
  updateValue,
}: {
  field: FieldOption;
  value: boolean;
  isSaving: boolean;
  isDisabled: boolean;
  updateValue: (fieldName: string, nextValue: boolean) => void;
}) {
  const { permissions } = useUserConfiguration();
  const canEditSettings = permissions.includes("backoffice.change_settings");
  const labelId = encodeURIComponent(field.name);
  return (
    <div
      data-testid="additional-field-container"
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div id={labelId} className="margin--bottom--xxs fontWeight--semibold">
            {field.label || field.name}
          </div>
          <div className="margin--bottom--s" style={{ color: "var(--color-mediumGrey)" }}>
            {field.description}
          </div>
        </div>
        <div>
          <Toggle
            labelledBy={labelId}
            onChange={(next) => {
              updateValue(field.name, next);
            }}
            isActive={value}
            isLoading={isSaving}
            disabled={isDisabled || !canEditSettings}
          />
        </div>
      </div>
      <hr className="margin--top--m" style={{ marginLeft: 0, marginRight: 0 }} />
    </div>
  );
}

export default LendingAskAdditionalField;
